import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton } from '../../components/common/Primitives';
import { getTeamPortrait } from '../../lib/teamPortraits';
import { ExternalLink } from 'lucide-react';

const LEADERSHIP = { name: 'Dr. Keyur Sorathia', role: 'Principal Investigator · Associate Professor, Department of Design, IIT Guwahati', gradient: 'from-royal to-purple' };

const CORE = [
  { name: 'Ganesh Kumar',    role: 'Lead Unity Developer', gradient: 'from-royal to-purple' },
  { name: 'Abhishek Manraj', role: 'UX Designer',           gradient: 'from-cyan to-royal' },
  { name: 'Trishala Jain',   role: 'UX Designer',           gradient: 'from-mint to-cyan' },
  { name: 'Sudeep Gunda',    role: '3D Artist',             gradient: 'from-purple to-cyan' },
  { name: 'Namita Deka',     role: 'Education Expert',       gradient: 'from-royal to-cyan' },
  { name: 'Bhaswati Sarma',  role: 'Education Expert',       gradient: 'from-mint to-royal' },
  { name: 'Oindrilla Deb',   role: 'Education Expert',       gradient: 'from-purple to-royal' },
  { name: 'Chetan Kashyap',  role: 'Education Expert',       gradient: 'from-cyan to-mint' },
];

function PortraitTile({ name, gradient }) {
  const url = getTeamPortrait(name);
  if (url) {
    return (
      <div className="aspect-square rounded-xl overflow-hidden bg-canvas-100">
        <img src={url} alt={name} loading="lazy" className="h-full w-full object-cover object-top" />
      </div>
    );
  }
  return (
    <div className={`aspect-square rounded-xl bg-gradient-to-br ${gradient} grid place-items-center text-white text-[11px] font-bold`}>PHOTO</div>
  );
}

export default function Team() {
  return (
    <PageShell>
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">Team and collaborators</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">Gyan Dhara is developed through collaboration between design researchers, UX designers, VR developers, technical artists, education experts and school educators.</p>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="LEADERSHIP" title="Project leadership" desc="Current leadership and institutional responsibility." />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-white p-3 shadow-soft">
              <PortraitTile name={LEADERSHIP.name} gradient={LEADERSHIP.gradient} />
              <div className="mt-3">
                <div className="text-[14.5px] font-bold text-navy">{LEADERSHIP.name}</div>
                <div className="mt-1 text-[12px] text-ink-500 leading-snug">{LEADERSHIP.role}</div>
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-canvas-200 p-6 shadow-soft">
              <Eyebrow color="purple">ABOUT DR. KEYUR SORATHIA</Eyebrow>
              <div className="mt-2 text-[16px] font-bold text-navy">Educator, applied researcher and a proud father</div>
              <p className="mt-3 text-[13.5px] text-ink-700 leading-relaxed">
                Dr. Keyur Sorathia is an Associate Professor in the Department of Design at IIT Guwahati, holding a Ph.D. in Human-Computer Interaction from the same institute. His work sits at the intersection of User Experience (UX) Design and HCI, with an active research focus on Virtual Reality for education and healthcare, and on Information and Communication Technology (ICT) for development.
              </p>
              <p className="mt-3 text-[13.5px] text-ink-700 leading-relaxed">
                He founded the Embedded Interaction Lab (EI Lab) at IIT Guwahati over a decade ago and, in 2023, co-founded MadVR — a VR-based surgical training and education company. He has also headed the Technology Incubation Center (TIC) at IIT Guwahati, mentoring and supporting new startups in their entrepreneurial journey.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">UX + HCI</span>
                <span className="rounded-full bg-royal-50 px-3 py-1 text-[11px] font-bold tracking-widest text-royal">VR FOR EDUCATION</span>
                <span className="rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-bold tracking-widest text-cyan">ICT FOR DEVELOPMENT</span>
                <span className="rounded-full bg-mint-50 px-3 py-1 text-[11px] font-bold tracking-widest text-mint-600">EI LAB · MadVR</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href="https://www.keyursorathia.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13px] font-semibold text-navy hover:bg-canvas-100 transition-colors">keyursorathia.com <ExternalLink className="h-3.5 w-3.5" /></a>
                <a href="https://www.ei-lab.in/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13px] font-semibold text-navy hover:bg-canvas-100 transition-colors">EI Lab <ExternalLink className="h-3.5 w-3.5" /></a>
                <a href="https://www.madvr.in/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13px] font-semibold text-navy hover:bg-canvas-100 transition-colors">MadVR <ExternalLink className="h-3.5 w-3.5" /></a>
              </div>
              <div className="mt-4 rounded-lg bg-canvas-100 p-3 text-[12px] text-ink-500">Currently on sabbatical leave until August 2026.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE TEAM */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="CORE TEAM" title="Current multidisciplinary contributors" />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {CORE.map((m,i)=>(
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-3 shadow-soft">
                <PortraitTile name={m.name} gradient={m.gradient} />
                <div className="mt-3">
                  <div className="text-[14px] font-bold text-navy">{m.name}</div>
                  <div className="mt-0.5 text-[12px] text-ink-500">{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
